import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import useFormInstance from "antd/es/form/hooks/useFormInstance";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import {
  EntityType,
  EntityTypesSupportingRoutes,
  EntityTypeSupportingLeadGen,
} from "@mapmycustomers/shared/types/entity";

import addCompaniesIcon from "@app/assets/map/leadFinder/addCompanies.svg";
import addPeopleIcon from "@app/assets/map/leadFinder/addPeople.svg";
import { initializeAddRecords } from "@app/component/leadFinder/store/actions";
import useDefaultSourceObjectKey from "@app/util/hook/useDefaultSourceObjectKey";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";

import styles from "./ModeSwitch.module.scss";

const messages = defineMessages({
  companies: {
    id: "leadFinder.addRecordsWizard.modeSwitch.companies",
    defaultMessage:
      "{sourceObject, select, standard {Companies} leadCompanies {Leads} siteCompanies {Sites} projectCompanies {Projects} shopCompanies {Shops} other {{label}}}",
    description: "Companies mode of the Lead Finder wizard, named after the source object",
  },
  people: {
    id: "leadFinder.addRecordsWizard.modeSwitch.people",
    defaultMessage:
      "{sourceObject, select, standard {People} partnerPeople {Partners} leadPeople {Leads} staffMemberPeople {Staff Members} leadPersonnel {Lead Personnel} other {{label}}}",
    description: "People mode of the Lead Finder wizard, named after the source object",
  },
});

interface Props {
  allowedEntityTypes: EntityTypeSupportingLeadGen[];
  onChange?: (value: EntityTypesSupportingRoutes) => void;
  onInitialize: typeof initializeAddRecords.request;
  value?: EntityTypesSupportingRoutes;
}

const ModeSwitch = ({ allowedEntityTypes, onChange, onInitialize, value }: Props) => {
  const intl = useIntl();

  const defaultCompanySourceObject = useDefaultSourceObjectKey(EntityType.COMPANY);
  const defaultPersonSourceObject = useDefaultSourceObjectKey(EntityType.PERSON);
  const companyModeArgs = useSourceObjectMessageArgs(
    EntityType.COMPANY,
    defaultCompanySourceObject
  );
  const personModeArgs = useSourceObjectMessageArgs(EntityType.PERSON, defaultPersonSourceObject);

  const form = useFormInstance();

  const handleChange = useCallback(
    (entityType: EntityTypesSupportingRoutes) => {
      if (value !== entityType) {
        onChange?.(entityType);
      }
    },
    [onChange, value]
  );

  useEffect(() => {
    if (value) {
      onInitialize({ entityType: value });
      form.setFieldValue("routeIdsOrRoutePayloads", []);
    }
  }, [form, onInitialize, value]);

  return (
    <div className={styles.container}>
      {allowedEntityTypes.includes(EntityType.COMPANY) && (
        <div
          className={cn(styles.btn, { [styles.btnActive]: value === EntityType.COMPANY })}
          onClick={() => handleChange(EntityType.COMPANY)}
        >
          <img
            alt={intl.formatMessage(messages.companies, companyModeArgs)}
            className={styles.icon}
            src={addCompaniesIcon}
          />
          <div className={styles.title}>
            {intl.formatMessage(messages.companies, companyModeArgs)}
          </div>
        </div>
      )}

      {allowedEntityTypes.includes(EntityType.PERSON) && (
        <div
          className={cn(styles.btn, { [styles.btnActive]: value === EntityType.PERSON })}
          onClick={() => handleChange(EntityType.PERSON)}
        >
          <img
            alt={intl.formatMessage(messages.people, personModeArgs)}
            className={styles.icon}
            src={addPeopleIcon}
          />
          <div className={styles.title}>{intl.formatMessage(messages.people, personModeArgs)}</div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onInitialize: initializeAddRecords.request,
};

export default connect(null, mapDispatchToProps)(ModeSwitch);
